import React from 'react'
import { Formik, FormikHelpers } from 'formik'

// Components
import { useToast } from 'containers/ToastManager'
import Form from 'components/Form'
import Modal from 'components/Modal'
import Button from 'components/Button'
import { ModalInjectedProps } from 'containers/ModalManager'

// Local Components
import BillingDetailsForm, {
  validationSchema,
} from 'modules/workspace/components/BillingDetailsForm'

// Types
import {
  useUpdateBillingDetailsMutation,
  BillingDetailsFragment,
} from 'gqlTypes'

export interface EditAddressDetailsModalProps extends ModalInjectedProps {
  customerId: string
  billingDetails: BillingDetailsFragment & {
    // This value should never be null in this modal
    address: NonNullable<BillingDetailsFragment['address']>
  }
}

type FormValues = {
  name: string
  email: string
  address: string
  city: string
  postalCode: string
  country: string
  taxId: string
  state: string
}

/**
 * EditAddressDetailsModal
 *
 * Renders the edit address modal in the Workspace Settings > Billing Page
 * within the Payment Information panel
 */
const EditAddressDetailsModal: React.FC<EditAddressDetailsModalProps> = ({
  customerId,
  billingDetails,
  hideModal,
}) => {
  const { showToast } = useToast()

  // MUTATIONS
  const [updateBillingDetails, { loading }] = useUpdateBillingDetailsMutation({
    onCompleted: () => {
      showToast('Your address information has been updated')
      hideModal()
    },
    onError: error => {
      showToast(error.message, 'negative')
    },
  })

  const handleOnSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    const {
      name,
      email,
      address,
      city,
      postalCode,
      country,
      taxId,
      state,
    } = values

    await updateBillingDetails({
      variables: {
        input: {
          customerId,
          email,
          name,
          taxId,
          address: {
            city,
            country,
            line1: address,
            postalCode,
            state,
          },
        },
      },
    })

    actions.setSubmitting(false)
  }

  const { name, email, address, taxId } = billingDetails

  const prefilledFormValues = {
    name,
    email,
    taxId: taxId?.id || '',
    address: address.line1,
    city: address.city,
    postalCode: address.postalCode,
    country: address.country,
    state: address?.state || '',
  }

  return (
    <Modal onCancel={hideModal}>
      <Formik
        initialValues={prefilledFormValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}>
        {({ errors, values, isSubmitting }) => (
          <Form>
            <Modal.Header>Billing Details</Modal.Header>
            <Modal.Body>
              <BillingDetailsForm<FormValues> />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                disabled={isSubmitting}
                onClick={hideModal}
                type="button">
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
                loading={isSubmitting || loading}>
                Update Billing Details
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default EditAddressDetailsModal
